import type { RangeValue } from "@sellar-io/components";
import { DiscoveryV2ListParameterModifierType } from "@sellar-io/api-v2/types";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { querystring } from "zustand-querystring";
import { useDiscoveryV2SearchHistoryStore } from "./DiscoveryV2SearchHistoryStore";
import { PriceType } from "./filterCards/FilterPriceTypes";
import { FilterCategoryKey } from "~/components/marketSearch/marketSearchFilters";

export {
  genericContainerTypes,
  specificContainerTypes,
  dietaryTypes,
  dietaryTypeValues,
} from "~/components/marketSearch/marketSearchFilterOptions";
export type {
  GenericContainerTypes,
  SpecificContainerTypes,
  ContainerType,
  DietaryType,
} from "~/components/marketSearch/marketSearchFilterOptions";
import type {
  ContainerType,
  DietaryType,
} from "~/components/marketSearch/marketSearchFilterOptions";
export const abvFilterType = {
  nonAlcoholic: {
    name: "0.0 - 0.5%",
    query: {
      type: "abv",
      modifier: "BETWEEN",
      value: { low: 0, high: 0.5 },
    },
  },
  lessThan2: {
    name: "0.5 - 2.0%",
    query: { type: "abv", modifier: "LESS_THAN", value: 2 },
  },
  between2And3: {
    name: "2.0 - 3.0%",
    query: { type: "abv", modifier: "BETWEEN", value: { low: 2, high: 3 } },
  },
  between3And4: {
    name: "3.0 - 4.0%",
    query: { type: "abv", modifier: "BETWEEN", value: { low: 3, high: 4 } },
  },
  between4And5: {
    name: "4.0 - 5.0%",
    query: { type: "abv", modifier: "BETWEEN", value: { low: 4, high: 5 } },
  },
  between5And6: {
    name: "5.0 - 6.0%",
    query: { type: "abv", modifier: "BETWEEN", value: { low: 5, high: 6 } },
  },
  between6And7: {
    name: "6.0 - 7.0%",
    query: { type: "abv", modifier: "BETWEEN", value: { low: 6, high: 7 } },
  },
  between7And8: {
    name: "7.0 - 8.0%",
    query: { type: "abv", modifier: "BETWEEN", value: { low: 7, high: 8 } },
  },
  between8And9: {
    name: "8.0 - 9.0%",
    query: { type: "abv", modifier: "BETWEEN", value: { low: 8, high: 9 } },
  },
  over9: {
    name: "Over 9.0%",
    query: { type: "abv", modifier: "GREATER_THAN_OR_EQUAL", value: 9 },
  },
} as const satisfies Record<
  string,
  {
    name: string;
    query: Extract<DiscoveryV2ListParameterModifierType, { type: "abv" }>;
  }
>;

export type AbvType = keyof typeof abvFilterType;

export const orderByTypes = {
  popular: "Popular on Sellar",
  updatedAt: "Recently added",
  bestsellers: "Bestsellers",
} as const;
export type OrderByType = keyof typeof orderByTypes;

export type DeliveredBySellarFilterOption =
  | "all"
  | "weekend"
  | "next-week"
  | "week-after-next";

type DiscoveryV2SearchStoreState = {
  searchValue: string;
  styleSearchValues: string[];
  menuActive: boolean;
  selectedSuppliers: { id: number; name: string }[];
  supplierId: number | undefined;
  selectedProductId: number | undefined;
  selectedProductParentId: number | undefined;
  isInStockItemsOnly: boolean;
  selectedContainerTypes: ContainerType[];
  isMySuppliersOnly: boolean;
  isSellarCreditEligibleOnly: boolean;
  isDirectDelivery: boolean;
  selectedDietaryTypes: DietaryType[];
  selectedAbvTypes: AbvType[];
  selectedPriceTypes: PriceType[];
  orderBy: OrderByType;
  categoryKey: FilterCategoryKey | undefined;
  isDeliveredBySellar: boolean;
  deliveredBySellarOption: DeliveredBySellarFilterOption;
  orderCutOffAt: string;
  isAvailableToPreOrder: boolean;
  abvRange: RangeValue | undefined;
  priceRange: RangeValue | undefined;
};

type DiscoveryV2SearchStoreActions = {
  setSearchValue: (searchValue: string) => void;
  addStyleSearchValue: (styleSearchValue: string) => void;
  setStyleSearchValues: (styleSearchValues: string[]) => void;
  setMenuActive: (active: boolean) => void;
  addSelectedSupplier: (supplier: { id: number; name: string }) => void;
  removeSelectedSupplier: (supplierId: number) => void;
  setSelectedSuppliers: (suppliers: { id: number; name: string }[]) => void;
  setSupplierId: (supplierId: number) => void;
  setSelectedProductIds: (
    selectedProductIds:
      | {
          productItemId: number;
          parentProductId: number | undefined;
        }
      | undefined,
  ) => void;
  setIsInStockItemsOnly: (isInStockItemsOnly: boolean) => void;
  addContainerType: (containerType: ContainerType) => void;
  removeContainerType: (containerType: ContainerType) => void;
  setContainerTypes: (containerTypes: ContainerType[]) => void;
  setIsMySuppliersOnly: (isMySuppliersOnly: boolean) => void;
  setIsSellarCreditEligibleOnly: (isSellarCreditEligibleOnly: boolean) => void;
  addDietaryType: (dietaryType: DietaryType) => void;
  removeDietaryType: (dietaryType: DietaryType) => void;
  setIsDirectDelivery: (isDirectDelivery: boolean) => void;
  addAbvType: (abvType: AbvType) => void;
  removeAbvType: (abvType: AbvType) => void;
  addPriceType: (priceType: PriceType) => void;
  removePriceType: (priceType: PriceType) => void;
  setPriceTypes: (priceTypes: PriceType[]) => void;
  setOrderBy: (orderBy: OrderByType) => void;
  setCategoryKey: (categoryKey: FilterCategoryKey | undefined) => void;
  reset: () => void;
  setIsDeliveredBySellar: (isDeliveredBySellar: boolean) => void;
  setDeliveredBySellarOption: (
    deliveredBySellarOption: DeliveredBySellarFilterOption,
  ) => void;
  setOrderCutOffAt: (orderCutOffAt: string | undefined) => void;
  setDietaryTypes: (dietaryTypes: DietaryType[]) => void;
  setAbvRange: (abv: RangeValue | undefined) => void;
  setIsAvailableToPreOrder: (isAvailableToPreOrder: boolean) => void;
  setPriceRange: (price: RangeValue | undefined) => void;
};

export const initialDiscoveryV2SearchStoreState: DiscoveryV2SearchStoreState = {
  searchValue: "",
  styleSearchValues: [],
  menuActive: false,
  selectedSuppliers: [],
  supplierId: undefined,
  selectedProductId: undefined,
  selectedProductParentId: undefined,
  isInStockItemsOnly: true,
  selectedContainerTypes: [],
  selectedDietaryTypes: [],
  isMySuppliersOnly: false,
  isSellarCreditEligibleOnly: false,
  isDirectDelivery: false,
  selectedAbvTypes: [],
  selectedPriceTypes: [],
  orderBy: "popular",
  categoryKey: undefined,
  isDeliveredBySellar: false,
  deliveredBySellarOption: "all",
  orderCutOffAt: "",
  abvRange: undefined,
  isAvailableToPreOrder: false,
  priceRange: undefined,
};

type DiscoveryV2SearchStoreType = DiscoveryV2SearchStoreState &
  DiscoveryV2SearchStoreActions;

export const useDiscoveryV2SearchStore = create<DiscoveryV2SearchStoreType>()(
  subscribeWithSelector(
    querystring<DiscoveryV2SearchStoreType>(
      (set, get) => ({
        ...initialDiscoveryV2SearchStoreState,
        setSearchValue: (searchValue) => {
          const prevValue = get().searchValue;

          if (prevValue !== searchValue) {
            useDiscoveryV2SearchHistoryStore.getState().add(searchValue);
            set({ searchValue });
          }
        },
        addStyleSearchValue: (styleSearchValue) =>
          set(({ styleSearchValues }) => ({
            styleSearchValues: Array.from(
              new Set(styleSearchValues).add(styleSearchValue),
            ),
          })),
        setStyleSearchValues: (styleSearchValues) => set({ styleSearchValues }),
        setMenuActive: (menuActive) => set({ menuActive }),
        addSelectedSupplier: (supplier) =>
          set(({ selectedSuppliers }) => ({
            selectedSuppliers: [
              ...selectedSuppliers.filter(
                (selectedSupplier) => selectedSupplier.id !== supplier.id,
              ),
              supplier,
            ],
          })),
        removeSelectedSupplier: (supplierId) =>
          set(({ selectedSuppliers }) => ({
            selectedSuppliers: selectedSuppliers.filter(
              (supplier) => supplier.id !== supplierId,
            ),
          })),
        setSelectedSuppliers: (suppliers) =>
          set({ selectedSuppliers: suppliers }),
        setSupplierId: (supplierId) => set({ supplierId }),
        setSelectedProductIds: (selectedProductIds) => {
          set({ selectedProductId: selectedProductIds?.productItemId });
          set({ selectedProductParentId: selectedProductIds?.parentProductId });
        },
        setIsInStockItemsOnly: (isInStockItemsOnly) =>
          set({ isInStockItemsOnly }),
        addContainerType: (containerType) =>
          set(({ selectedContainerTypes }) => ({
            selectedContainerTypes: Array.from(
              new Set(selectedContainerTypes).add(containerType),
            ),
          })),
        removeContainerType: (containerType) =>
          set(({ selectedContainerTypes: prev }) => {
            const next = new Set(prev);
            next.delete(containerType);
            return { selectedContainerTypes: Array.from(next) };
          }),
        setContainerTypes: (containerTypes) =>
          set({ selectedContainerTypes: containerTypes }),
        setIsMySuppliersOnly: (isMySuppliersOnly) => set({ isMySuppliersOnly }),
        setIsSellarCreditEligibleOnly: (isSellarCreditEligibleOnly) =>
          set({ isSellarCreditEligibleOnly }),
        addDietaryType: (dietaryType) =>
          set(({ selectedDietaryTypes }) => ({
            selectedDietaryTypes: Array.from(
              new Set(selectedDietaryTypes).add(dietaryType),
            ),
          })),
        removeDietaryType: (dietaryType) =>
          set(({ selectedDietaryTypes: prev }) => {
            const next = new Set(prev);
            next.delete(dietaryType);
            return { selectedDietaryTypes: Array.from(next) };
          }),
        setIsDirectDelivery: (isDirectDelivery) =>
          set({ isDirectDelivery: isDirectDelivery }),
        addAbvType: (abvType) =>
          set(({ selectedAbvTypes }) => ({
            selectedAbvTypes: Array.from(
              new Set(selectedAbvTypes).add(abvType),
            ),
          })),
        removeAbvType: (abvType) =>
          set(({ selectedAbvTypes: prev }) => {
            const next = new Set(prev);
            next.delete(abvType);
            return { selectedAbvTypes: Array.from(next) };
          }),
        addPriceType: (priceType) =>
          set(({ selectedPriceTypes }) => ({
            selectedPriceTypes: Array.from(
              new Set(selectedPriceTypes).add(priceType),
            ),
          })),
        removePriceType: (priceType) =>
          set(({ selectedPriceTypes: prev }) => {
            const next = new Set(prev);
            next.delete(priceType);
            return { selectedPriceTypes: Array.from(next) };
          }),
        setPriceTypes: (priceTypes) => set({ selectedPriceTypes: priceTypes }),
        setOrderBy: (orderBy) => set({ orderBy }),
        setCategoryKey: (categoryKey) => set({ categoryKey }),
        reset: () => set(initialDiscoveryV2SearchStoreState),
        setIsDeliveredBySellar: (isDeliveredBySellar) =>
          set({ isDeliveredBySellar }),
        setDeliveredBySellarOption: (deliveredBySellarOption) =>
          set({ deliveredBySellarOption }),
        setOrderCutOffAt: (orderCutOffAt) => set({ orderCutOffAt }),
        setDietaryTypes: (dietaryTypes) =>
          set({ selectedDietaryTypes: dietaryTypes }),
        setAbvRange: (abvRange) => set({ abvRange }),
        setIsAvailableToPreOrder: (isAvailableToPreOrder) =>
          set({ isAvailableToPreOrder }),
        setPriceRange: (priceRange) => set({ priceRange }),
      }),
      {
        select(pathname) {
          const persistParam = pathname.includes("/discover");
          return {
            searchValue: persistParam,
            styleSearchValues: persistParam,
            menuActive: persistParam,
            selectedSuppliers: persistParam,
            supplierId: persistParam,
            selectedProductId: persistParam,
            selectedProductParentId: persistParam,
            isInStockItemsOnly: persistParam,
            selectedContainerTypes: persistParam,
            selectedDietaryTypes: persistParam,
            isMySuppliersOnly: persistParam,
            isSellarCreditEligibleOnly: persistParam,
            isDirectDelivery: persistParam,
            selectedAbvTypes: persistParam,
            selectedPriceTypes: persistParam,
            orderBy: persistParam,
            categoryKey: persistParam,
            isDeliveredBySellar: persistParam,
            deliveredBySellarOption: persistParam,
            orderCutOffAt: persistParam,
            abvRange: persistParam,
            priceRange: persistParam,
            isAvailableToPreOrder: persistParam,
          };
        },
      },
    ),
  ),
);
